import React, { useState, useEffect } from 'react';
import { bool, func, string } from 'prop-types';

import { connect } from 'react-redux';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';

import EsmsDialog from '../../components/EsmsDialog';

import * as ArchitectActions from '../../actions/architectActions';
import * as ApplicationMessageActions from '../../actions/applicationMessageActions';
import * as UiActions from '../../actions/uiActions';

import classes from './CreateEntityModal.module.css';

const ValidationSchema = Yup.object().shape({
    systemName: Yup.array().required()
});

const CreateEquipmentSystemModal = ({
    close,
    clearErrors,
    companyInfo,
    open,
    filteredSystems,
    selectedNode,
    selectedSite,
    title,
    isLoading,
    errors,
    systems,
    toggleLoading,
    ...props
}) => {

    const [availableSystems, setAvailableSystems] = useState([]);

    useEffect(() => {
        const normalized = filteredSystems.map(({ id }) => id);
        setAvailableSystems(systems.filter(value => !normalized.includes(value.id)));
    }, [systems, filteredSystems]);

    const formik = useFormik({
        initialValues: {
            systemName: []
        },
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {
            const {
                createEquipmentSystem
            } = props;

            let isCreated;

            clearErrors();
            setSubmitting(true);

            try {
                if (!isLoading) {
                    toggleLoading(true);
                    isCreated = await createEquipmentSystem(
                        selectedNode.id,
                        selectedSite.id,
                        formik.values.systemName
                    );
                }

                if (isCreated) {
                    setSubmitting(false);
                    toggleLoading(false);

                    handleClose();
                } else {
                    setSubmitting(false);
                    toggleLoading(false);
                }
            } catch (error) {
                setSubmitting(false);
            }
        }
    });

    const handleClose = () => {
        toggleLoading(false);
        clearErrors();

        close();
    };

    const handleSystemName = () => {
        let ids = [...formik.values.systemName];
        let names = [];

        systems.forEach(piece => {
            if (ids.includes(piece.id)) {
                names.push(piece.name);
            }
        });

        return names.join(', ');
    };

    return (
        <EsmsDialog
            errors={errors}
            open={open}
            onClose={handleClose}
            title={title}
            onRendered={formik.handleReset}
            onConfirm={formik.handleSubmit}
            disabled={formik.isSubmitting}
            isLoading={isLoading}
            confirmText="Add"
        >
            <div className={classes.formColumn}>
                <div className={classes.labelColumn}>
                    <div className={classes.labelHeader}>
                        <img src="/assets/images/equipmentTreeIcon.svg" alt="equipment icon" />
                        <span>Add Equipment to Systems</span>
                    </div>
                    <div className={classes.labelContent}>
                        <p>
                            Select the Systems to assign the piece of Equipment to.
                        </p>
                        <p>
                            Systems already associated with this piece of Equipment will not appear in the list.
                        </p>
                    </div>
                </div>
                <div className={classes.inputColumn}>
                    <div className={classes.inputs}>
                        <FormControl className={classes.fullWidth}>
                            <InputLabel
                                htmlFor="systemName"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                                error={Boolean(formik.errors.systemName) && Boolean(formik.touched.systemName)}
                            >
                                Select Systems
                            </InputLabel>
                            <Select
                                multiple
                                value={formik.values.systemName}
                                id="systemName"
                                input={<Input id="select-multiple-checkbox" />}
                                renderValue={handleSystemName}
                                onChange={formik.handleChange}
                                name="systemName"
                                className={classes.input}
                                inputProps={{
                                    onBlur: formik.handleBlur
                                }}
                                error={Boolean(formik.errors.systemName) && Boolean(formik.touched.systemName)}
                            >
                                {
                                    availableSystems.map(piece => (
                                        <MenuItem
                                            className={classes.selectMenuItem}
                                            value={piece.id}
                                            key={piece.id}
                                        >
                                            <Checkbox
                                                color="primary"
                                                checked={formik.values.systemName.indexOf(piece.id) > -1}
                                            />
                                            <span>{piece.name}</span>
                                        </MenuItem>
                                    ))
                                }
                            </Select>
                            {formik.touched.systemName &&
                             formik.errors.systemName && <FormHelperText className={classes.error}>{formik.errors.systemName}</FormHelperText>}
                        </FormControl>
                    </div>
                </div>
            </div>
        </EsmsDialog >
    );
};

CreateEquipmentSystemModal.propTypes = {
    close: func.isRequired,
    open:  bool.isRequired,
    title: string.isRequired
};

const mapStateToProps = state => {
    return {
        systems:         state.architect.systems,
        companyInfo:     state.companyInfo,
        errors:          state.messages.errors,
        filteredSystems: state.architect.filteredSystems,
        isLoading:       state.ui.isLoading,
        selectedNode:    state.architect.selectedNode,
        selectedSite:    state.site.selectedSite
    };
};

const mapDispatchToProps = {
    ...ArchitectActions,
    ...ApplicationMessageActions,
    ...UiActions
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateEquipmentSystemModal);
